import { useMutation, useQueryClient } from 'react-query'
import { API_METHODS } from 'constants/api-methods'
import { BASE_API_URL } from 'constants/env'
import { useApiRequest } from 'hooks/use-api-request'
import { AppleId } from 'interfaces/apple-id'
import toast from 'react-hot-toast'

interface UpdateAppleIdParams {
  id: string | number
  data: Partial<AppleId>
}

interface UpdateAppleIdResponse {
  message: string
  apple_id: AppleId
}

const useUpdateAppleId = () => {
  const api = useApiRequest()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({ id, data }: UpdateAppleIdParams) => {
      return api<UpdateAppleIdResponse>({
        url: `${BASE_API_URL}/admin/api/v2/apple_ids/${id}`,
        apiMethod: API_METHODS.PUT,
        data: { apple_id: data },
      })
    },
    {
      onSuccess: async (data, variables) => {
        await queryClient.invalidateQueries(['apple_id_by_user_id'])
        await queryClient.invalidateQueries(['apple_id_details', variables.id])
      },
      onError: async (error: Error) => {
        const errorMessage = error.message || 'Unknown error occurred'
        toast.error(`Failed to update Apple ID details: ${errorMessage}`)
      },
    }
  )

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  }
}

export default useUpdateAppleId
